import { useSession } from 'next-auth/react';
import React, { useEffect, useState } from 'react';

import { Heading } from '@/components/heading';
import { ShareForm } from '@/components/modules/share/form';
import { LoadingSpinner } from '@/components/ui/loading/spinner';
import { UserMessage } from '@/components/ui/user-message';
import { SharedItemModel } from '@/lib/models/shared-item/types';
import { getSharedItemUrl } from '@/lib/models/shared-item/utils';
import { copyToClipboard } from '@/utils/helpers';
import { getUserOrg } from '@/utils/permissions/helpers';
import { trackUser } from '@/utils/tracking';

export interface ShareProps {
  shareUrlOverride?: string;
  heading?: string;
  sharedItem?: SharedItemModel | null;
  isLoading?: boolean;
  error?: string;
  onUpdate: (sharedItem: SharedItemModel) => void;
}

const Share = ({ sharedItem, shareUrlOverride, heading, onUpdate, isLoading, error }: ShareProps) => {
  const { data: session } = useSession();
  const [shouldRestrictToOrg, setShouldRestrictToOrg] = useState(false);
  const org = session ? getUserOrg(session.user) : null;
  const origin = process.env.NEXT_PUBLIC_SITE_URL || '';

  const shareUrl = shareUrlOverride || `${origin}${getSharedItemUrl(sharedItem)}`;

  const handleShareToOrgToggle = (shouldRestrict: boolean) => {
    if (!sharedItem) return;

    trackUser.event(`Toggled shipment list sharing to ${shouldRestrictToOrg ? 'org' : 'everyone'}`);
    onUpdate({ ...sharedItem, shouldRestrictToOrg: shouldRestrict });
    setShouldRestrictToOrg(shouldRestrict);
  };

  const handleCopy = () => {
    trackUser.event('Clicked copy shipment sharing url');
    copyToClipboard(shareUrl);
  };

  useEffect(() => {
    if (sharedItem) {
      setShouldRestrictToOrg(Boolean(sharedItem.shouldRestrictToOrg));
    }
  }, [sharedItem]);

  return (
    <>
      {error ? <UserMessage variant="error" body={error} /> : null}
      {isLoading ? <LoadingSpinner isVisible /> : null}
      {!isLoading && !error ? (
        <>
          {heading ? (
            <Heading variant="h3" as="h2">
              {heading}
            </Heading>
          ) : null}
          <ShareForm
            url={shareUrl}
            onClickShareOption={handleShareToOrgToggle}
            shouldRestrictToOrg={shouldRestrictToOrg}
            onCopy={handleCopy}
            orgName={org?.name || ''}
          />
        </>
      ) : null}
    </>
  );
};

export { Share };
